import React, { FC, useMemo } from 'react';
import { ProductMetaData, ProductSorting } from '../../../../models/Product';
import { getLoanFormats, LoanFormat, mapParsedLoanFormat } from '../../../../models/LoanFormat';
import { FilterSetting, ProductFilter } from '../../../store/api';
import { BackIssues, CatalogueProductGroup, LanguageLearningProductGroup, LoteProductGroup } from '../ProductGroup';
import RoutingUtils from '../../utils/utils-routing';
import Utils from '../../utils';
import {
    PRODUCT_CLASS_CAMPAIGN,
    PRODUCT_CLASS_LANGUAGE_LEARNING,
    PRODUCT_CLASS_LOCAL,
    PRODUCT_CLASS_LOTE,
    PRODUCT_CLASS_STANDARD
} from '../../../store/constants';
import { useTranslation } from 'react-i18next';
import { MULTI_LANGUAGE_SHOP, useFeature } from '../../utils/togglz';
import MetadataUtils from '../../utils/utils-metadata';
import useShopLanguages from '../../hooks/useShopLanguages';
import { ALL_LANGUAGES } from '../../../shop/language/constants';
import { LOANFORMAT_EPRESS_OBJECT } from '../../loanFormat/constants';
import { To } from 'react-router-dom';
import { ShopLanguageDetails } from '../../../app-store/authentication/authenticationReducer';

type Props = {
    readonly product: ProductMetaData;
};

// TODO [MultiLanguageShop] Remove this component when MultiLanguageShop is enabled
export const ProductDetailProductGroups: FC<Props> = ({ product }) => {
    const productLoanFormat = mapParsedLoanFormat(product.loanFormat);
    const otherLoanFormats = getLoanFormats().filter(loanFormat => loanFormat !== productLoanFormat);

    return (
        <>
            {productLoanFormat === LOANFORMAT_EPRESS_OBJECT.name ? <BackIssues product={product} /> : null}
            <SeriesProductGroup
                loanFormat={productLoanFormat}
                product={product}
                dataSource={'catalogue'}
                language={'none'}
            />
            <AuthorProductGroup
                loanFormat={productLoanFormat}
                product={product}
                dataSource={'catalogue'}
                language={'none'}
            />
            <GenreProductGroup
                loanFormat={productLoanFormat}
                product={product}
                dataSource={'catalogue'}
                language={'none'}
            />

            {otherLoanFormats.map(loanFormat => (
                <React.Fragment key={loanFormat}>
                    <SeriesProductGroup
                        loanFormat={loanFormat}
                        product={product}
                        dataSource={'catalogue'}
                        language={'none'}
                    />
                    <AuthorProductGroup
                        loanFormat={loanFormat}
                        product={product}
                        dataSource={'catalogue'}
                        language={'none'}
                    />
                    <GenreProductGroup
                        loanFormat={loanFormat}
                        product={product}
                        dataSource={'catalogue'}
                        language={'none'}
                    />
                </React.Fragment>
            ))}
        </>
    );
};

// TODO [MultiLanguageShop] Remove this redundant component when MultiLanguageShop is enabled
export const ForeignLanguageProductDetailProductGroups: FC<Props> = ({ product }) => {
    const productLoanFormat = mapParsedLoanFormat(product.loanFormat);
    const otherLoanFormats = getLoanFormats().filter(loanFormat => loanFormat !== productLoanFormat);
    const dataSource = product.productClass === PRODUCT_CLASS_LOTE ? 'lote' : 'languagelearning';

    return (
        <>
            {productLoanFormat === LOANFORMAT_EPRESS_OBJECT.name ? <BackIssues product={product} /> : null}

            <SeriesProductGroup
                loanFormat={productLoanFormat}
                product={product}
                language={'product'}
                dataSource={dataSource}
            />
            <AuthorProductGroup
                loanFormat={productLoanFormat}
                product={product}
                language={'product'}
                dataSource={dataSource}
            />
            <GenreProductGroup
                loanFormat={productLoanFormat}
                product={product}
                language={'product'}
                dataSource={dataSource}
            />

            <SeriesProductGroup
                loanFormat={productLoanFormat}
                product={product}
                language={'other'}
                dataSource={dataSource}
            />
            <AuthorProductGroup
                loanFormat={productLoanFormat}
                product={product}
                language={'other'}
                dataSource={dataSource}
            />
            <GenreProductGroup
                loanFormat={productLoanFormat}
                product={product}
                language={'other'}
                dataSource={dataSource}
            />

            {otherLoanFormats.map(loanFormat => (
                <React.Fragment key={loanFormat}>
                    <SeriesProductGroup
                        loanFormat={loanFormat}
                        product={product}
                        language={'product'}
                        dataSource={dataSource}
                    />
                    <AuthorProductGroup
                        loanFormat={loanFormat}
                        product={product}
                        language={'product'}
                        dataSource={dataSource}
                    />
                    <GenreProductGroup
                        loanFormat={loanFormat}
                        product={product}
                        language={'product'}
                        dataSource={dataSource}
                    />
                    <SeriesProductGroup
                        loanFormat={loanFormat}
                        product={product}
                        language={'other'}
                        dataSource={dataSource}
                    />
                    <AuthorProductGroup
                        loanFormat={loanFormat}
                        product={product}
                        language={'other'}
                        dataSource={dataSource}
                    />
                    <GenreProductGroup
                        loanFormat={loanFormat}
                        product={product}
                        language={'other'}
                        dataSource={dataSource}
                    />
                </React.Fragment>
            ))}
        </>
    );
};

export const MultiLanguageShopProductDetailProductGroups: FC<Props> = ({ product }) => {
    const { shopLanguages } = useShopLanguages();
    const productLoanFormat = mapParsedLoanFormat(product.loanFormat);

    const dataSource = product.productClass === PRODUCT_CLASS_LANGUAGE_LEARNING ? 'languagelearning' : 'catalogue';

    const otherBookFormat = productLoanFormat === LoanFormat.eBooks ? LoanFormat.eAudiobooks : LoanFormat.eBooks;
    const otherShopLanguages = shopLanguages.filter(
        ({ languageCodes }) => !product.languageCode || !languageCodes.includes(product.languageCode)
    );

    return productLoanFormat === LoanFormat.ePress ? (
        <>
            <BackIssues product={product} />
            <GenreProductGroup
                loanFormat={productLoanFormat}
                product={product}
                dataSource={dataSource}
                language={'product'}
            />
            {otherShopLanguages.map(language => (
                <GenreProductGroupSL
                    key={`genre-${language}`}
                    loanFormat={productLoanFormat}
                    product={product}
                    dataSource={dataSource}
                    shopLanguage={language}
                />
            ))}
            <GenreProductGroup
                loanFormat={productLoanFormat}
                product={product}
                dataSource={dataSource}
                language={'other'}
            />
            <GenreProductGroup
                loanFormat={LoanFormat.eBooks}
                product={product}
                language={'product'}
                dataSource={dataSource}
            />
            <GenreProductGroup
                loanFormat={LoanFormat.eAudiobooks}
                product={product}
                language={'product'}
                dataSource={dataSource}
            />
        </>
    ) : (
        <>
            <SeriesProductGroup
                loanFormat={productLoanFormat}
                product={product}
                dataSource={dataSource}
                language={'product'}
            />
            <AuthorProductGroup
                loanFormat={productLoanFormat}
                product={product}
                dataSource={dataSource}
                language={'product'}
            />
            {otherShopLanguages.map(language => (
                <AuthorProductGroupSL
                    key={`author-${language}`}
                    loanFormat={productLoanFormat}
                    product={product}
                    dataSource={dataSource}
                    shopLanguage={language}
                />
            ))}
            <AuthorProductGroup
                loanFormat={productLoanFormat}
                product={product}
                dataSource={dataSource}
                language={'other'}
            />
            <SeriesProductGroup
                loanFormat={otherBookFormat}
                product={product}
                dataSource={dataSource}
                language={'product'}
            />
            <AuthorProductGroup
                loanFormat={otherBookFormat}
                product={product}
                dataSource={dataSource}
                language={'product'}
            />
            {otherShopLanguages.map(language => (
                <AuthorProductGroupSL
                    key={`author-${language}`}
                    loanFormat={otherBookFormat}
                    product={product}
                    dataSource={dataSource}
                    shopLanguage={language}
                />
            ))}
            <AuthorProductGroup
                loanFormat={otherBookFormat}
                product={product}
                dataSource={dataSource}
                language={'other'}
            />
            <GenreProductGroup
                loanFormat={productLoanFormat}
                product={product}
                dataSource={dataSource}
                language={'product'}
            />
            <GenreProductGroup
                loanFormat={otherBookFormat}
                product={product}
                dataSource={dataSource}
                language={'product'}
            />
            <GenreProductGroup
                loanFormat={LoanFormat.ePress}
                product={product}
                dataSource={dataSource}
                language={'product'}
            />
        </>
    );
};

type SeriesProductGroupProps = {
    readonly loanFormat: LoanFormat;
    readonly product: ProductMetaData;
    readonly language: 'product' | 'other' | 'none';
    readonly dataSource: 'catalogue' | 'lote' | 'languagelearning';
};

const SeriesProductGroup: FC<SeriesProductGroupProps> = ({ loanFormat, product, language, dataSource }) => {
    const { shopLanguages } = useShopLanguages();
    const productLoanFormat = mapParsedLoanFormat(product.loanFormat);
    const languageFilter = useMemo(() => buildLanguageFilter(product, language), [product, language]);
    const { t } = useTranslation();
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
    const shopLanguage = isMultiLanguageShopEnabled
        ? language === 'other'
            ? 'other'
            : language === 'product'
            ? shopLanguages.find(sl => product.languageCode && sl.languageCodes.includes(product.languageCode))
                  ?.shopLanguageId
            : undefined
        : undefined;

    const productSpec = useMemo(
        () => ({
            loanFormat,
            filters: {
                series: { include: [product.seriesName!] },
                productId: { exclude: [product.id] },
                ...languageFilter
            },
            sortBy: ProductSorting.SERIES_NUMBER_HIGH_TO_LOW
        }),
        [product, languageFilter, loanFormat]
    );

    const seeAllUrl = buildSeeAllUrl({
        product,
        loanFormat,
        filter: productSpec.filters,
        isMultiLanguageShopEnabled,
        shopLanguage,
        sortBy: ProductSorting.SERIES_NUMBER_HIGH_TO_LOW
    });

    const titlePrefix = loanFormat === productLoanFormat ? t('components.productDetails.more') : '';

    const title = t(`components.productDetails.moreFrom`, {
        titlePrefix,
        loanFormat: t(`components.productDetails.${loanFormat}`),
        type: product.seriesName
    }).trim();

    if (!product.seriesName) {
        return null;
    }

    switch (dataSource) {
        case 'lote':
            return <LoteProductGroup productSpec={productSpec} seeAllUrl={seeAllUrl} title={title} />;
        case 'languagelearning':
            return (
                <LanguageLearningProductGroup
                    productSpec={productSpec}
                    seeAllUrl={seeAllUrl}
                    title={title}
                    shopLanguage={shopLanguage}
                />
            );
        default:
            return (
                <CatalogueProductGroup
                    productSpec={productSpec}
                    seeAllUrl={seeAllUrl}
                    title={title}
                    shopLanguage={shopLanguage}
                />
            );
    }
};

type AuthorProductGroupProps = {
    readonly loanFormat: LoanFormat;
    readonly product: ProductMetaData;
    readonly language: 'product' | 'other' | 'none';
    readonly dataSource: 'catalogue' | 'lote' | 'languagelearning';
};

const AuthorProductGroup: FC<AuthorProductGroupProps> = ({ loanFormat, product, language, dataSource }) => {
    const { shopLanguages, showLote } = useShopLanguages();
    const productLoanFormat = mapParsedLoanFormat(product.loanFormat);
    const languageFilter = useMemo(() => buildLanguageFilter(product, language), [product, language]);
    const { t } = useTranslation();
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
    const shopLanguage = isMultiLanguageShopEnabled
        ? language === 'other'
            ? 'other'
            : language === 'product'
            ? shopLanguages.find(sl => product.languageCode && sl.languageCodes.includes(product.languageCode))
                  ?.shopLanguageId
            : undefined
        : undefined;

    const authors = useMemo(() => (product.authors ? product.authors.filter(a => !!a.id) : []), [product.authors]);

    const authorNames = authors.map(a => MetadataUtils.getPerformerFullname(a)).join(', ');
    const authorIds = useMemo(() => authors.map(a => a.id!), [authors]);

    const productSpec = useMemo(
        () => ({
            loanFormat,
            filters: {
                author: { include: authorIds },
                productId: { exclude: [product.id] },
                ...languageFilter
            }
        }),
        [product, authorIds, languageFilter, loanFormat]
    );

    const seeAllUrl = buildSeeAllUrl({
        product,
        loanFormat,
        filter: productSpec.filters,
        isMultiLanguageShopEnabled,
        shopLanguage
    });

    const isForOtherLanguage = language === 'other';
    const titlePrefix =
        loanFormat === productLoanFormat && !isForOtherLanguage ? t('components.productDetails.more') : '';

    const title = t(`components.productDetails.moreFrom`, {
        context: isForOtherLanguage ? (showLote ? 'lote' : 'otherlanguages') : undefined,
        titlePrefix,
        loanFormat: t(`components.productDetails.${loanFormat}`),
        type: authorNames
    }).trim();

    if (!product.authors || product.authors.length === 0) {
        return null;
    }

    switch (dataSource) {
        case 'lote':
            return <LoteProductGroup productSpec={productSpec} seeAllUrl={seeAllUrl} title={title} />;
        case 'languagelearning':
            return (
                <LanguageLearningProductGroup
                    productSpec={productSpec}
                    seeAllUrl={seeAllUrl}
                    title={title}
                    shopLanguage={shopLanguage}
                />
            );
        default:
            return (
                <CatalogueProductGroup
                    productSpec={productSpec}
                    seeAllUrl={seeAllUrl}
                    title={title}
                    shopLanguage={shopLanguage}
                />
            );
    }
};

type AuthorProductGroupSLProps = {
    readonly loanFormat: LoanFormat;
    readonly product: ProductMetaData;
    readonly shopLanguage: Pick<ShopLanguageDetails, 'shopLanguageId' | 'shopLanguageName'>;
    readonly dataSource: 'catalogue' | 'lote' | 'languagelearning';
};

const AuthorProductGroupSL: FC<AuthorProductGroupSLProps> = ({ loanFormat, product, shopLanguage, dataSource }) => {
    const productLoanFormat = mapParsedLoanFormat(product.loanFormat);
    const { t } = useTranslation();

    const authors = useMemo(() => (product.authors ? product.authors.filter(a => !!a.id) : []), [product.authors]);

    const authorNames = authors.map(a => MetadataUtils.getPerformerFullname(a)).join(', ');
    const authorIds = useMemo(() => authors.map(a => a.id!), [authors]);

    const productSpec = useMemo(
        () => ({
            loanFormat,
            filters: {
                author: { include: authorIds },
                productId: { exclude: [product.id] }
            }
        }),
        [product, authorIds, loanFormat]
    );

    const seeAllUrl = buildSeeAllUrl({
        product,
        loanFormat,
        filter: productSpec.filters,
        isMultiLanguageShopEnabled: true,
        shopLanguage: shopLanguage.shopLanguageId
    });

    const titlePrefix = loanFormat === productLoanFormat ? t('components.productDetails.more') : '';

    const title = t(`components.productDetails.moreFrom`, {
        context: 'language',
        titlePrefix,
        loanFormat: t(`components.productDetails.${loanFormat}`),
        type: authorNames,
        language: t(`menu.shop.shopLanguages.${shopLanguage.shopLanguageId}`, shopLanguage.shopLanguageName)
    }).trim();

    if (!product.authors || product.authors.length === 0) {
        return null;
    }

    switch (dataSource) {
        case 'lote':
            return <LoteProductGroup productSpec={productSpec} seeAllUrl={seeAllUrl} title={title} />;
        case 'languagelearning':
            return (
                <LanguageLearningProductGroup
                    productSpec={productSpec}
                    seeAllUrl={seeAllUrl}
                    title={title}
                    shopLanguage={shopLanguage.shopLanguageId}
                />
            );
        default:
            return (
                <CatalogueProductGroup
                    productSpec={productSpec}
                    seeAllUrl={seeAllUrl}
                    title={title}
                    shopLanguage={shopLanguage.shopLanguageId}
                />
            );
    }
};

type GenreProductGroupProps = {
    readonly loanFormat: LoanFormat;
    readonly product: ProductMetaData;
    readonly language: 'product' | 'other' | 'none';
    readonly dataSource: 'catalogue' | 'lote' | 'languagelearning';
};

const GenreProductGroup: FC<GenreProductGroupProps> = ({ loanFormat, product, language, dataSource }) => {
    const { showLote, shopLanguages } = useShopLanguages();
    const languageFilter = useMemo(() => buildLanguageFilter(product, language), [product, language]);
    const { t } = useTranslation();
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
    const shopLanguage = isMultiLanguageShopEnabled
        ? language === 'other'
            ? 'other'
            : language === 'product'
            ? shopLanguages.find(sl => product.languageCode && sl.languageCodes.includes(product.languageCode))
                  ?.shopLanguageId
            : undefined
        : undefined;

    const filteredGenres = useMemo(
        () =>
            product.genres
                // TODO hacky language genre filter...
                ?.filter(g => g.id.indexOf('@@Language') < 0) ?? [],
        [product.genres]
    );
    const genreIds = useMemo(
        () => filteredGenres.map(g => (g.subGenres && g.subGenres.length > 0 ? g.subGenres[0].id : g.id)),
        [filteredGenres]
    );
    const localisedGenreIds = useMemo(
        () =>
            filteredGenres
                .map(g => (g.subGenres && g.subGenres.length > 0 ? `${g.name} \u27E9 ${g.subGenres[0].name}` : g.name))
                .join(', '),
        [filteredGenres]
    );

    const productSpec = useMemo(
        () => ({
            loanFormat,
            filters: {
                genre: { include: genreIds },
                productId: { exclude: [product.id] },
                ageCategory: product.ageCategory ? { include: [product.ageCategory] } : {},
                ...languageFilter
            },
            ageCategory: product.ageCategory
        }),
        [loanFormat, genreIds, product, languageFilter]
    );

    const seeAllUrl = buildSeeAllUrl({
        product,
        loanFormat,
        filter: productSpec.filters,
        isMultiLanguageShopEnabled,
        shopLanguage
    });

    const isForOtherLanguage = language === 'other';
    const titlePrefix =
        loanFormat === LoanFormat.ePress && !isForOtherLanguage ? t('components.productDetails.more') : '';

    const title = t(`components.productDetails.moreFrom`, {
        context: isForOtherLanguage ? (showLote ? 'lote' : 'otherlanguages') : undefined,
        titlePrefix,
        loanFormat: t(`components.productDetails.${loanFormat}`),
        type: localisedGenreIds
    }).trim();

    if (!product.genres || product.genres.length === 0) {
        return null;
    }

    switch (dataSource) {
        case 'lote':
            return <LoteProductGroup productSpec={productSpec} seeAllUrl={seeAllUrl} title={title} />;
        case 'languagelearning':
            return (
                <LanguageLearningProductGroup
                    productSpec={productSpec}
                    seeAllUrl={seeAllUrl}
                    title={title}
                    shopLanguage={shopLanguage}
                />
            );
        default:
            return (
                <CatalogueProductGroup
                    productSpec={productSpec}
                    seeAllUrl={seeAllUrl}
                    title={title}
                    shopLanguage={shopLanguage}
                />
            );
    }
};

type GenreProductGroupSLProps = {
    readonly loanFormat: LoanFormat;
    readonly product: ProductMetaData;
    readonly shopLanguage: Pick<ShopLanguageDetails, 'shopLanguageId' | 'shopLanguageName'>;
    readonly dataSource: 'catalogue' | 'lote' | 'languagelearning';
};

const GenreProductGroupSL: FC<GenreProductGroupSLProps> = ({ loanFormat, product, shopLanguage, dataSource }) => {
    const { t } = useTranslation();

    const filteredGenres = useMemo(
        () =>
            product.genres
                // TODO hacky language genre filter...
                ?.filter(g => g.id.indexOf('@@Language') < 0) ?? [],
        [product.genres]
    );
    const genreIds = useMemo(
        () => filteredGenres.map(g => (g.subGenres && g.subGenres.length > 0 ? g.subGenres[0].id : g.id)),
        [filteredGenres]
    );
    const localisedGenreIds = useMemo(
        () =>
            filteredGenres
                .map(g => (g.subGenres && g.subGenres.length > 0 ? `${g.name} \u27E9 ${g.subGenres[0].name}` : g.name))
                .join(', '),
        [filteredGenres]
    );

    const productSpec = useMemo(
        () => ({
            loanFormat,
            filters: {
                genre: { include: genreIds },
                productId: { exclude: [product.id] },
                ageCategory: product.ageCategory ? { include: [product.ageCategory] } : {}
            },
            ageCategory: product.ageCategory
        }),
        [loanFormat, genreIds, product]
    );

    const seeAllUrl = buildSeeAllUrl({
        product,
        loanFormat,
        filter: productSpec.filters,
        isMultiLanguageShopEnabled: true,
        shopLanguage: shopLanguage.shopLanguageId
    });

    const titlePrefix = loanFormat === LoanFormat.ePress ? t('components.productDetails.more') : '';

    const title = t(`components.productDetails.moreFrom`, {
        context: 'language',
        titlePrefix,
        loanFormat: t(`components.productDetails.${loanFormat}`),
        type: localisedGenreIds,
        language: t(`menu.shop.shopLanguages.${shopLanguage.shopLanguageId}`, shopLanguage.shopLanguageName)
    }).trim();

    if (!product.genres || product.genres.length === 0) {
        return null;
    }

    switch (dataSource) {
        case 'lote':
            return <LoteProductGroup productSpec={productSpec} seeAllUrl={seeAllUrl} title={title} />;
        case 'languagelearning':
            return (
                <LanguageLearningProductGroup
                    productSpec={productSpec}
                    seeAllUrl={seeAllUrl}
                    title={title}
                    shopLanguage={shopLanguage.shopLanguageId}
                />
            );
        default:
            return (
                <CatalogueProductGroup
                    productSpec={productSpec}
                    seeAllUrl={seeAllUrl}
                    title={title}
                    shopLanguage={shopLanguage.shopLanguageId}
                />
            );
    }
};

interface BuildSeeAllUrlProps {
    readonly product: ProductMetaData;
    readonly loanFormat: LoanFormat;
    readonly filter: { [key: string]: FilterSetting };
    isMultiLanguageShopEnabled: boolean;
    shopLanguage: string | undefined;
    sortBy?: string;
}

const buildSeeAllUrl = ({
    product,
    loanFormat,
    filter,
    isMultiLanguageShopEnabled,
    shopLanguage,
    sortBy
}: BuildSeeAllUrlProps): To => {
    const mappedProductClass =
        isMultiLanguageShopEnabled && product.productClass === PRODUCT_CLASS_LOTE
            ? PRODUCT_CLASS_STANDARD
            : product.productClass;
    switch (mappedProductClass) {
        case PRODUCT_CLASS_STANDARD:
            return Utils.catalogueLocationDescriptorString({
                loanFormat,
                filter,
                shopLanguage,
                isMultiLanguageShopEnabled,
                ageCategory: undefined,
                sortBy
            });
        case PRODUCT_CLASS_LOTE:
            return RoutingUtils.foreignLanguageDescriptorString({
                language: ALL_LANGUAGES,
                loanFormat,
                filter,
                ageCategory: undefined,
                sortBy
            });
        case PRODUCT_CLASS_LANGUAGE_LEARNING:
            return RoutingUtils.languageLearningDescriptorString({
                language: ALL_LANGUAGES,
                loanFormat,
                filter,
                shopLanguage,
                isMultiLanguageShopEnabled,
                ageCategory: undefined,
                sortBy
            });
        case PRODUCT_CLASS_CAMPAIGN:
            return Utils.catalogueLocationDescriptorString({
                loanFormat,
                filter,
                shopLanguage: undefined,
                isMultiLanguageShopEnabled,
                ageCategory: undefined,
                sortBy
            });
        case PRODUCT_CLASS_LOCAL:
            return '';
    }
};

const buildLanguageFilter = (
    product: ProductMetaData,
    language: 'product' | 'other' | 'none'
): ProductFilter | undefined => {
    if (language === 'none') {
        return undefined;
    }

    const productLanguage = product.languageCode ?? product.language;

    if (!productLanguage) {
        return undefined;
    }

    const filterField = product.languageCode ? 'languageCode' : 'language';

    return language === 'product'
        ? { [filterField]: { include: [productLanguage] } }
        : { [filterField]: { exclude: [productLanguage] } };
};
